import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { logger } from "@/logger";

/**
 * Authentication Client Configuration
 *
 * This file sets up the Better Auth client for our application.
 *
 * NOTE: This is a temporary setup. In the future, we plan to refactor the authentication system to
 * remove the dependency on the Better Auth client. The goal is to use our custom SessionProvider
 * exclusively for managing authentication state and actions.
 *
 * Current setup:
 *
 * - Uses createAuthClient from Better Auth library
 * - Configures the base URL for API requests
 * - Utilizes the inferAdditionalFields plugin to define extra user fields:
 *
 *   - Firstname (string)
 *   - Lastname (string)
 *   - Permissions (string array)
 * - Sends X-App-Slug header on ALL requests for multi-tenant support:
 *
 *   - Works in both web browsers and Capacitor apps (capacitor:// protocol)
 *   - Backend uses this to route users to correct whitelabel app
 *   - Configured via global fetchOptions.headers
 *
 * @todo Refactor authentication to use custom SessionProvider without Better Auth client
 */
export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_BACKEND_URL || "/",
  plugins: [
    inferAdditionalFields({
      user: {
        firstname: {
          type: "string",
        },
        lastname: {
          type: "string",
        },
        permissions: {
          type: "string[]",
        },
      },
    }),
  ],
  fetchOptions: {
    headers: {
      "x-app-slug": getCurrentAppSlug(),
    },
  },
  advanced: {
    cookiePrefix: "boomer",
  },
  session: {
    useSecureCookies: process.env.NODE_ENV !== "development",
  },
});

/**
 * Sets a cookie to track which app the password reset was initiated from. This enables multi-tenant
 * password reset flow where users can be redirected back to the correct whitelabel app after
 * clicking the email link.
 *
 * The cookie is read by the backend during password reset email generation and included in the
 * email link as a query parameter.
 *
 * @example
 *   ```typescript
 *   // Before calling authClient.forgetPassword()
 *   setPasswordResetAppCookie('custom-app-b');
 *   await authClient.forgetPassword({ email: 'user@example.com' });
 *   ```;
 *
 * @param appSlug - The organization/app slug (e.g., 'boomer', 'custom-app-b')
 */
/**
 * Checks if cookies should use secure attributes based on build environment.
 *
 * @returns True for production builds, false for development
 */
export function isProductionEnvironment(): boolean {
  return import.meta.env.MODE === "production";
}

/**
 * Returns the current app/organization slug.
 *
 * Returns empty string if VITE_ORGANIZATION_SLUG is not set.
 */
export function getCurrentAppSlug(): string {
  return import.meta.env.VITE_ORGANIZATION_SLUG || "";
}

/**
 * Returns the URL scheme for local development deep link testing.
 *
 * **Purpose:** Maps organization slugs (from VITE_ORGANIZATION_SLUG) to their actual registered URL
 * schemes for native iOS/Android apps. This allows testing deep links locally with custom
 * protocols.
 *
 * **Why this is needed:** Each whitelabel app registers a custom URL scheme in:
 *
 * - IOS: Info.plist CFBundleURLSchemes
 * - Android: build.gradle manifestPlaceholders.customUrlScheme
 *
 * These URL schemes follow a specific naming convention that differs from the organization slug.
 *
 * **How it works:**
 *
 * 1. Takes the current app's organization slug (e.g., "jongens-van-de-bar")
 * 2. Removes all hyphens → "jongensvandebar"
 * 3. Adds "boomer-" prefix → "boomer-jongensvandebar"
 *
 * **Examples:**
 *
 * - VITE_ORGANIZATION_SLUG="boomer" → URL scheme: "boomer://"
 * - VITE_ORGANIZATION_SLUG="jongens-van-de-bar" → URL scheme: "boomer-jongensvandebar://"
 * - VITE_ORGANIZATION_SLUG="fixt" → URL scheme: "boomer-fixt://"
 * - VITE_ORGANIZATION_SLUG="the-edge-events" → URL scheme: "boomer-theedgeevents://"
 *
 * **Local development only:** This is ONLY used for testing deep links locally. Production uses
 * HTTPS web URLs (https://api.heyboomer.nl/deeplink/...) which work across all platforms without
 * custom URL schemes.
 *
 * **Testing deep links locally:**
 *
 * ```bash
 * # For Jongens van de Bar app
 * xcrun simctl openurl booted boomer-jongensvandebar://accept-invitation/TOKEN
 *
 * # For Boomer app
 * xcrun simctl openurl booted boomer://accept-invitation/TOKEN
 * ```
 *
 * @returns The registered URL scheme (without "://") for the current app
 */
export function getLocalDevUrlScheme(): string {
  const slug = getCurrentAppSlug();

  // Default boomer app uses "boomer" scheme
  if (slug === "boomer") {
    return "boomer";
  }

  // Map organization slugs to URL schemes (remove hyphens, add boomer- prefix)
  const schemeSlug = slug.replace(/-/g, "");
  return `boomer-${schemeSlug}`;
}

/**
 * Sets a cookie to track the current app/organization slug.
 *
 * Cookie name:
 *
 * - Development: "boomer.app_slug"
 * - Production: "__Secure-boomer.app_slug"
 */
export function setAppSlugCookie(appSlug: string): void {
  const isProduction = isProductionEnvironment();

  const baseCookieName = "boomer.app_slug";
  const cookieName = isProduction ? `__Secure-${baseCookieName}` : baseCookieName;

  const backendUrl = import.meta.env.VITE_BACKEND_URL || "/";
  const domain = getCookieDomainFromBackendUrl(backendUrl);

  // Cookie attributes
  const secureFlag = isProduction ? "; Secure" : "";
  const sameSite = "; SameSite=Lax"; // Lax allows cookie to be sent on navigation from email
  const path = "; Path=/"; // Available across the entire domain
  const maxAge = "; Max-Age=3600"; // 1 hour expiration (matches backend TTL used for reset)
  // Omit Domain attribute for IP addresses (browsers reject it)
  const domainAttr = domain ? `; Domain=${domain}` : "";

  // Build the cookie string
  const cookieString = `${cookieName}=${encodeURIComponent(appSlug)}${path}${maxAge}${sameSite}${domainAttr}${secureFlag}`;

  // Set the cookie
  document.cookie = cookieString;

  // Verify it was set and log for debugging
  const wasSet = document.cookie.includes(cookieName);
  logger.info(
    {
      appSlug,
      isProduction,
      cookieName,
      backendUrl,
      domain,
      cookieString,
      wasSet,
      allCookies: document.cookie,
    },
    "[setAppSlugCookie]",
  );

  if (!wasSet) {
    logger.warn(
      `[setAppSlugCookie] Cookie "${cookieName}" was not set! This might be due to: 1. __Secure- prefix requires HTTPS, 2. SameSite restrictions, 3. Browser security settings`,
    );
  }
}

/**
 * Derives the cookie domain from the Better Auth backend URL.
 *
 * - For normal domains, returns the registrable domain with leading dot e.g.
 *   "https://api.heyboomer.localhost" -> ".heyboomer.localhost"
 * - For IPv4 addresses, returns null (Domain attribute must be omitted for IPs) e.g.
 *   "http://192.168.31.53:3000" -> null
 *
 * @returns The domain string to use in the Domain attribute, or null if Domain should be omitted
 */
export function getCookieDomainFromBackendUrl(backendUrl?: string): string | null {
  const url = backendUrl || import.meta.env.VITE_BACKEND_URL || "/";
  const backendHostname = new URL(url, window.location.origin).hostname;

  const isIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(backendHostname);

  // Browsers reject Domain attribute for IP addresses - must omit it
  if (isIPv4) {
    return null;
  }

  const parts = backendHostname.split(".");
  return parts.length >= 2 ? `.${parts.slice(-2).join(".")}` : backendHostname;
}

/**
 * @deprecated Use setAppSlugCookie instead. This function now delegates to setAppSlugCookie while
 *   preserving the same behavior for existing callers.
 */
export function setPasswordResetAppCookie(appSlug: string): void {
  setAppSlugCookie(appSlug);
}
